import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { formatCurrency, formatNumber } from '../../lib/utils';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import { Search, Filter, Download, Eye, UserX, UserCheck, Lock, Unlock, Key, Shield, X, ChevronDown, RefreshCw, Plus, Minus, Wallet, AlertCircle, CheckCircle } from 'lucide-react';

const statusFilters = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'inactive', label: 'Inactive' },
];

const kycFilters = [
  { value: '', label: 'All KYC' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'NOT_SUBMITTED', label: 'Not Submitted' },
  { value: 'REJECTED', label: 'Rejected' },
];

export default function AdminUsersPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState<'credit' | 'debit'>('credit');
  const [adjustSuccess, setAdjustSuccess] = useState('');
  const [adjustError, setAdjustError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['adminUsers', search, statusFilter, kycFilter, page],
    queryFn: () => api.get('/admin/users', { params: { search, status: statusFilter, kycStatus: kycFilter, page, limit: 20 } }).then(r => r.data),
  });

  const { data: adjustmentHistory } = useQuery({
    queryKey: ['walletAdjustments', selectedUser?.id],
    queryFn: () => api.get(`/admin/wallet/${selectedUser.id}/adjustments`).then(r => r.data),
    enabled: !!selectedUser && detailTab === 'wallet',
  });

  const suspendMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/users/${id}/suspend`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminUsers'] }); setSelectedUser(null); },
  });
  const activateMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/users/${id}/activate`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminUsers'] }); setSelectedUser(null); },
  });
  const freezeMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/users/${id}/freeze-wallet`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminUsers'] }); },
  });

  const adjustMutation = useMutation({
    mutationFn: (data: { userId: string; amount: number; reason: string }) => api.post('/admin/wallet/adjust', data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['walletAdjustments'] });
      setAdjustSuccess(`Wallet adjusted successfully. New balance: ${formatCurrency(res.data.newBalance)}`);
      setAdjustError('');
      setAdjustmentAmount('');
      setAdjustmentReason('');
      setTimeout(() => setAdjustSuccess(''), 4000);
    },
    onError: (err: any) => {
      setAdjustError(err.response?.data?.message || 'Adjustment failed');
      setAdjustSuccess('');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (data: { userId: string; newPassword: string }) => api.patch(`/admin/users/${data.userId}/password`, { newPassword: data.newPassword }),
    onSuccess: () => {
      setPwSuccess('Password updated successfully');
      setPwError('');
      setNewPassword('');
      setTimeout(() => { setPwSuccess(''); setShowPasswordModal(false); }, 2000);
    },
    onError: (err: any) => {
      setPwError(err.response?.data?.message || 'Failed to change password');
      setPwSuccess('');
    },
  });

  const users = data?.data || [];
  const meta = data?.meta || { total: 0, page: 1, totalPages: 1 };

  const detailTabs = ['overview', 'wallet', 'investments', 'referrals', 'kyc', 'security'];

  const handleAdjust = () => {
    const amt = parseFloat(adjustmentAmount);
    if (isNaN(amt) || amt <= 0) { setAdjustError('Enter a valid amount'); return; }
    if (!adjustmentReason.trim() || adjustmentReason.trim().length < 3) { setAdjustError('Reason is required (minimum 3 characters)'); return; }
    setAdjustError('');
    setAdjustSuccess('');
    const finalAmount = adjustmentType === 'debit' ? -amt : amt;
    adjustMutation.mutate({ userId: selectedUser.id, amount: finalAmount, reason: adjustmentReason.trim() });
  };

  const openAdjustment = (type: 'credit' | 'debit') => {
    setAdjustmentType(type);
    setAdjustmentAmount('');
    setAdjustmentReason('');
    setAdjustError('');
    setAdjustSuccess('');
    setShowAdjustment(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-sm text-slate-400">{meta.total} total users</p>
        </div>
        <Button variant="secondary" size="sm"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
      </div>

      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input type="text" placeholder="Search by name, email, username, mobile..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-9 pr-4 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.02] text-white placeholder-slate-500" />
            </div>
            <Select options={statusFilters} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} />
            <Select options={kycFilters} value={kycFilter} onChange={e => { setKycFilter(e.target.value); setPage(1); }} />
            <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStatusFilter(''); setKycFilter(''); setPage(1); }}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" />Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Mobile</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">KYC</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Balance</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Joined</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {isLoading ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">Loading...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No users found</td></tr>
                ) : users.map((u: any) => (
                  <tr key={u.id} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => { setSelectedUser(u); setDetailTab('overview'); }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-500/10 rounded-full flex items-center justify-center text-xs font-bold text-primary-400">
                          {(u.profile?.fullName || u.username)?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{u.profile?.fullName || u.username}</p>
                          <p className="text-xs text-slate-500">@{u.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">{u.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{u.mobile}</td>
                    <td className="px-4 py-3"><Badge status={u.isSuspended ? 'SUSPENDED' : u.isActive ? 'ACTIVE' : 'INACTIVE'} /></td>
                    <td className="px-4 py-3"><Badge status={u.kycStatus} /></td>
                    <td className="px-4 py-3 text-sm font-medium text-white">{formatCurrency(u.wallets?.[0]?.balance || 0)}</td>
                    <td className="px-4 py-3 text-sm text-slate-400">{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(u); setDetailTab('overview'); }}><Eye className="w-3.5 h-3.5" /></Button>
                        {u.isSuspended ? (
                          <Button variant="ghost" size="sm" onClick={() => activateMutation.mutate(u.id)}><UserCheck className="w-3.5 h-3.5 text-emerald-400" /></Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => suspendMutation.mutate(u.id)}><UserX className="w-3.5 h-3.5 text-red-400" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {meta.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
              <p className="text-sm text-slate-400">Page {meta.page} of {meta.totalPages}</p>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="secondary" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={!!selectedUser} onClose={() => { setSelectedUser(null); setShowAdjustment(false); }} title="User Detail">
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-white/[0.06]">
              <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center text-lg font-bold text-primary-400">
                {(selectedUser.profile?.fullName || selectedUser.username)?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-bold text-white">{selectedUser.profile?.fullName || selectedUser.username}</p>
                <p className="text-sm text-slate-400">@{selectedUser.username} | {selectedUser.email}</p>
              </div>
            </div>

            <div className="flex gap-1 border-b border-white/[0.06]">
              {detailTabs.map(tab => (
                <button key={tab} onClick={() => setDetailTab(tab)}
                  className={`px-3 py-2 text-xs font-medium capitalize border-b-2 transition-colors ${detailTab === tab ? 'border-primary-400 text-primary-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
                  {tab}
                </button>
              ))}
            </div>

            {detailTab === 'overview' && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Mobile</p><p className="font-medium text-white">{selectedUser.mobile}</p></div>
                  <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Country</p><p className="font-medium text-white">{selectedUser.profile?.country || '-'}</p></div>
                  <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Status</p><Badge status={selectedUser.isSuspended ? 'SUSPENDED' : 'ACTIVE'} /></div>
                  <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">KYC</p><Badge status={selectedUser.kycStatus} /></div>
                  <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Balance</p><p className="font-medium text-white">{formatCurrency(selectedUser.wallets?.[0]?.balance || 0)}</p></div>
                  <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Referral Code</p><p className="font-medium font-mono text-xs text-white">{selectedUser.referralCode}</p></div>
                  <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Joined</p><p className="font-medium text-white">{new Date(selectedUser.createdAt).toLocaleDateString()}</p></div>
                  <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Last Login</p><p className="font-medium text-white">{selectedUser.lastLoginAt ? new Date(selectedUser.lastLoginAt).toLocaleDateString() : 'Never'}</p></div>
                </div>
              </div>
            )}

            {detailTab === 'overview' && (
              <div className="flex gap-2 pt-3 border-t border-white/[0.06]">
                {selectedUser.isSuspended ? (
                  <Button size="sm" onClick={() => activateMutation.mutate(selectedUser.id)}><UserCheck className="w-3.5 h-3.5 mr-1" />Activate</Button>
                ) : (
                  <Button size="sm" variant="danger" onClick={() => suspendMutation.mutate(selectedUser.id)}><UserX className="w-3.5 h-3.5 mr-1" />Suspend</Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => freezeMutation.mutate(selectedUser.id)}><Lock className="w-3.5 h-3.5 mr-1" />Freeze Wallet</Button>
                <Button size="sm" variant="secondary" onClick={() => { setNewPassword(''); setPwError(''); setPwSuccess(''); setShowPasswordModal(true); }}><Key className="w-3.5 h-3.5 mr-1" />Reset Password</Button>
              </div>
            )}

            {detailTab === 'wallet' && (
              <div className="space-y-4">
                {selectedUser.wallets?.map((w: any) => (
                  <div key={w.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg">
                    <div><p className="text-sm font-medium text-white">{w.currency} Wallet</p><p className="text-xs text-slate-400">Locked: {formatCurrency(w.locked)}</p></div>
                    <p className="text-lg font-bold text-white">{formatCurrency(w.balance)}</p>
                  </div>
                ))}

                <div className="flex gap-2">
                  <Button size="sm" onClick={() => openAdjustment('credit')}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Add Funds
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => openAdjustment('debit')}>
                    <Minus className="w-3.5 h-3.5 mr-1" />Subtract Funds
                  </Button>
                </div>

                {showAdjustment && (
                  <div className={`p-4 rounded-xl border ${adjustmentType === 'credit' ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                        {adjustmentType === 'credit' ? <Plus className="w-4 h-4 text-emerald-400" /> : <Minus className="w-4 h-4 text-red-400" />}
                        {adjustmentType === 'credit' ? 'Add Funds' : 'Subtract Funds'}
                      </h4>
                      <button onClick={() => setShowAdjustment(false)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
                    </div>

                    {adjustSuccess && (
                      <div className="flex items-center gap-2 p-2 mb-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-xs text-emerald-400">
                        <CheckCircle className="w-3.5 h-3.5 shrink-0" />{adjustSuccess}
                      </div>
                    )}
                    {adjustError && (
                      <div className="flex items-center gap-2 p-2 mb-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />{adjustError}
                      </div>
                    )}

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Amount (₹)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">₹</span>
                          <input type="number" min="1" step="any" value={adjustmentAmount}
                            onChange={e => setAdjustmentAmount(e.target.value)}
                            placeholder="0.00"
                            className="w-full pl-7 pr-4 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.04] text-white placeholder-slate-500" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Reason *</label>
                        <textarea value={adjustmentReason} onChange={e => setAdjustmentReason(e.target.value)}
                          placeholder="Enter reason for this adjustment (min 3 characters)" rows={2}
                          className="w-full px-3 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.04] text-white placeholder-slate-500 resize-none" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant={adjustmentType === 'credit' ? 'primary' : 'danger'} onClick={handleAdjust} isLoading={adjustMutation.isPending}>
                          {adjustmentType === 'credit' ? <Plus className="w-3.5 h-3.5 mr-1" /> : <Minus className="w-3.5 h-3.5 mr-1" />}
                          {adjustmentType === 'credit' ? 'Add Funds' : 'Subtract Funds'}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setShowAdjustment(false)}>Cancel</Button>
                      </div>
                    </div>
                  </div>
                )}

                {adjustmentHistory?.data?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-2">Adjustment History</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {adjustmentHistory.data.map((t: any) => (
                        <div key={t.id} className="flex items-center justify-between p-2.5 bg-white/[0.02] rounded-lg text-xs">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${t.type === 'ADMIN_CREDIT' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                              {t.type === 'ADMIN_CREDIT' ? <Plus className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                            </div>
                            <div>
                              <p className="text-white">{t.description}</p>
                              <p className="text-slate-500">{new Date(t.createdAt).toLocaleString()}</p>
                            </div>
                          </div>
                          <span className={`font-medium ${t.type === 'ADMIN_CREDIT' ? 'text-emerald-400' : 'text-red-400'}`}>
                            {t.type === 'ADMIN_CREDIT' ? '+' : '-'}{formatCurrency(t.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'investments' && <p className="text-sm text-slate-400 py-4">Investment data loads from API</p>}
            {detailTab === 'referrals' && <p className="text-sm text-slate-400 py-4">Referral data loads from API</p>}
            {detailTab === 'kyc' && <p className="text-sm text-slate-400 py-4">KYC documents load from API</p>}
            {detailTab === 'security' && <p className="text-sm text-slate-400 py-4">Security data loads from API</p>}
          </div>
        )}
      </Modal>

      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title="Reset User Password">
        <div className="space-y-4">
          <p className="text-sm text-slate-400">Set a new password for <span className="font-semibold text-white">{selectedUser?.username}</span></p>
          {pwSuccess && (
            <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-sm text-emerald-400">
              <CheckCircle className="w-4 h-4 shrink-0" />{pwSuccess}
            </div>
          )}
          {pwError && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0" />{pwError}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">New Password</label>
            <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="Minimum 8 characters" autoFocus
              className="w-full px-3 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/[0.04] text-white placeholder-slate-500" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => {
              if (!newPassword || newPassword.length < 8) { setPwError('Password must be at least 8 characters'); return; }
              setPwError('');
              passwordMutation.mutate({ userId: selectedUser.id, newPassword });
            }} isLoading={passwordMutation.isPending}>
              <Key className="w-3.5 h-3.5 mr-1" />Update Password
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
